
// src/utils/firebaseStorage.ts
import { firebaseService } from '../lib/firebase';
import { AppData, defaultData } from './storage';
import { NativeFeatures } from '../services/nativeFeatures';

// Load data from Firebase or localStorage with native storage fallback
export const loadFirebaseData = async (): Promise<AppData> => {
  
  // Try to load from Firebase if user is signed in
  const isSignedIn = firebaseService.isUserSignedIn();
  
  if (isSignedIn) {
    try {
      const firebaseData = await firebaseService.loadData();
      
      if (firebaseData) {
        console.log('Data loaded from Firebase');
        return firebaseData;
      }
    } catch (error) {
      console.error('[firebaseStorage.ts] loadFirebaseData - Error loading data from Firebase:', {
        error: error,
        message: error.message,
        stack: error.stack
      });
    }
  }
  
  // Fallback to native storage
  try {
    const nativeBackup = await NativeFeatures.getFromNativeStorage('starCollectorBackup');
    if (nativeBackup) {
      console.log('Data loaded from native storage backup');
      return nativeBackup;
    }
  } catch (error) {
    console.error('[firebaseStorage.ts] loadFirebaseData - Error loading data from native storage:', {
      error: error,
      message: error.message,
      stack: error.stack
    });
  }
  
  // Final fallback to localStorage
  try {
    const localData = localStorage.getItem('starRewardsTracker');
    
    if (localData) {
      const parsed = JSON.parse(localData);
      console.log('Data loaded from localStorage');
      return parsed;
    }
  } catch (error) {
    console.error('[firebaseStorage.ts] loadFirebaseData - Error loading data from localStorage:', {
      error: error,
      message: error.message,
      stack: error.stack
    });
  }
  
  console.log('No saved data found, using default data');
  return defaultData;
};

// Save data to Firebase, native storage, and localStorage
export const saveFirebaseData = async (data: AppData): Promise<void> => {
  
  // Always save to localStorage as a backup
  try {
    const dataString = JSON.stringify(data);
    localStorage.setItem('starRewardsTracker', dataString);
  } catch (error) {
    console.error('[firebaseStorage.ts] saveFirebaseData - Error saving to localStorage:', {
      error: error,
      message: error.message,
      stack: error.stack
    });
  }
  
  // Backup to native storage
  try {
    await NativeFeatures.saveToNativeStorage('starCollectorBackup', data);
  } catch (error) {
    console.error('[firebaseStorage.ts] saveFirebaseData - Error saving to native storage:', {
      error: error,
      message: error.message,
      stack: error.stack
    });
  }
  
  // Save to Firebase if user is signed in
  const isSignedIn = firebaseService.isUserSignedIn();
  
  if (isSignedIn) {
    try {
      const saveResult = await firebaseService.saveData(data);
      
      if (saveResult) {
        console.log('Data saved to Firebase successfully');
      } else {
        console.error('[firebaseStorage.ts] saveFirebaseData - Firebase save returned false');
      }
    } catch (error) {
      console.error('[firebaseStorage.ts] saveFirebaseData - Error saving to Firebase:', {
        error: error,
        message: error.message,
        stack: error.stack
      });
    }
  }
};

// Sync data between devices
export const syncData = async (): Promise<AppData | null> => {
  
  const isSignedIn = firebaseService.isUserSignedIn();
  
  if (!isSignedIn) {
    return null;
  }
  
  try {
    const firebaseData = await firebaseService.loadData();
    
    if (firebaseData) {
      
      try {
        const dataString = JSON.stringify(firebaseData);
        localStorage.setItem('starRewardsTracker', dataString);
        
        // Also update native storage backup
        await NativeFeatures.saveToNativeStorage('starCollectorBackup', firebaseData);
      } catch (localStorageError) {
        console.error('[firebaseStorage.ts] syncData - Error updating localStorage:', localStorageError);
      }
      
      return firebaseData;
    }
  } catch (error) {
    console.error('[firebaseStorage.ts] syncData - Error syncing data:', {
      error: error,
      message: error.message,
      stack: error.stack
    });
  }
  
  return null;
};
